/**
 * Lens Cards Editor
 *
 * Admin component for editing the 3 lens cards displayed in Explorer's One Key Insight modal.
 * Uses the refactored lens system with clean lens_label dropdown.
 */

import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface LensCard {
  id: string;
  song_id: string;
  civic_level: string;
  slot: number;
  lens_id: string;
  back_insight: string | null;
  source: 'auto' | 'manual';
}

interface Lens {
  id: string;
  lens_key: string;
  lens_label: string;
  description: string;
}

interface LensCardsEditorProps {
  songId: string;
  onSave?: () => void;
  onChange?: (hasChanges: boolean) => void;
}

export interface LensCardsEditorHandle {
  save: () => Promise<boolean>;
}

const LensCardsEditor = forwardRef<LensCardsEditorHandle, LensCardsEditorProps>(
  ({ songId, onSave, onChange }, ref) => {
  const [cards, setCards] = useState<LensCard[]>([]);
  const [originalCards, setOriginalCards] = useState<LensCard[]>([]);
  const [lenses, setLenses] = useState<Lens[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [songId]);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      // Load lenses
      const { data: lensData, error: lensError } = await supabase
        .from('insight_archetypes')
        .select('id, lens_key, lens_label, description')
        .eq('enabled', true)
        .order('sort_order');

      if (lensError) throw lensError;
      setLenses(lensData || []);

      // Load existing lens cards
      const { data: cardData, error: cardError } = await supabase
        .from('song_lens_cards')
        .select('*')
        .eq('song_id', songId)
        .eq('civic_level', 'explorer')
        .order('slot');

      if (cardError) throw cardError;

      // If no cards exist, create default empty cards
      if (!cardData || cardData.length === 0) {
        const defaultCards: LensCard[] = [1, 2, 3].map((slot) => ({
          id: `temp-${slot}`,
          song_id: songId,
          civic_level: 'explorer',
          slot,
          lens_id: '',
          back_insight: null,
          source: 'manual' as const,
        }));
        setCards(defaultCards);
        setOriginalCards(JSON.parse(JSON.stringify(defaultCards)));
      } else {
        setCards(cardData);
        setOriginalCards(JSON.parse(JSON.stringify(cardData)));
      }
    } catch (err: any) {
      console.error('[LensCardsEditor] Load error:', err);
      setError(err.message || 'Failed to load lens cards');
    } finally {
      setLoading(false);
    }
  }

  function handleCardChange(slot: number, field: keyof LensCard, value: any) {
    const updatedCards = cards.map((card) =>
      card.slot === slot ? { ...card, [field]: value } : card
    );
    setCards(updatedCards);
  }

  // Detect if there are unsaved changes
  function hasChanges(): boolean {
    if (cards.length !== originalCards.length) return true;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const originalCard = originalCards.find(c => c.slot === card.slot);

      if (!originalCard) return true;

      if (
        card.lens_id !== originalCard.lens_id ||
        card.back_insight !== originalCard.back_insight
      ) {
        return true;
      }
    }

    return false;
  }

  // Notify parent of changes
  useEffect(() => {
    if (!loading) {
      onChange?.(hasChanges());
    }
  }, [cards, loading]);

  // Expose save function to parent
  useImperativeHandle(
    ref,
    () => ({
      async save() {
        try {
          // Filter out empty cards
          const validCards = cards.filter(card => card.lens_id);

          if (validCards.length === 0) {
            return true; // Nothing to save
          }

          // Use upsert to handle both insert and update cases
          const cardsToSave = validCards.map(card => ({
            song_id: songId,
            civic_level: 'explorer',
            slot: card.slot,
            lens_id: card.lens_id,
            back_insight: card.back_insight || null,
            source: 'manual' as const,
          }));

          const { data, error } = await supabase
            .from('song_lens_cards')
            .upsert(cardsToSave, {
              onConflict: 'song_id,civic_level,slot'
            })
            .select();

          if (error) throw error;

          // Update card IDs with the real ones from the database
          if (data && data.length > 0) {
            setCards(prev => prev.map(card => {
              const savedCard = data.find(d => d.slot === card.slot);
              return savedCard ? { ...card, id: savedCard.id } : card;
            }));
          }

          // Update original cards to match current state
          setOriginalCards(JSON.parse(JSON.stringify(cards)));
          onSave?.();
          onChange?.(false);
          return true;
        } catch (err: any) {
          console.error('[LensCardsEditor] Save error:', err);
          setError(err.message || 'Failed to save changes');
          return false;
        }
      }
    }),
    [cards, songId]
  );


  if (loading) {
    return (
      <div className="p-6 text-center">
        <p className="text-[#A0A0A0]">Loading lens cards...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-[13px] text-[#A0A0A0]">
          Edit the 3 lens cards
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-[14px] text-red-400">{error}</p>
        </div>
      )}

      {/* Cards */}
      <div className="space-y-6">
        {cards.map((card) => {
          return (
            <div
              key={card.slot}
              className="p-6 bg-[#1A1A1A] border border-[#282828] rounded-xl space-y-4"
            >
              <h4 className="text-[14px] font-semibold text-[#F5F5F5]">
                Lens Card {card.slot}
              </h4>

              {/* Lens Dropdown */}
              <div>
                <label className="block text-[13px] font-medium text-[#F5F5F5] mb-2">
                  Lens
                </label>
                <select
                  value={card.lens_id}
                  onChange={(e) => handleCardChange(card.slot, 'lens_id', e.target.value)}
                  className="w-full pl-4 pr-12 py-2.5 bg-[#121212] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23A0A0A0%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_0.75rem_center] bg-no-repeat"
                >
                  <option value="">Select a lens...</option>
                  {lenses.map((lens) => (
                    <option key={lens.id} value={lens.id}>
                      {lens.lens_label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Back Insight */}
              <div>
                <label className="block text-[13px] font-medium text-[#F5F5F5] mb-2">
                  Back Insight
                </label>
                <textarea
                  value={card.back_insight || ''}
                  onChange={(e) => handleCardChange(card.slot, 'back_insight', e.target.value)}
                  placeholder="The insight text displayed on the back of the card"
                  rows={4}
                  className="w-full px-4 py-3 bg-[#121212] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] placeholder-[#505050] focus:outline-none focus:border-[#40E0D0] resize-none"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

LensCardsEditor.displayName = 'LensCardsEditor';

export default LensCardsEditor;
